import React from 'react';

export default function AboutUs() {
  const farukProfilePic = new URL('../assets/images/faruk-image.jpeg', import.meta.url).href;
  return (
    <div className="from-slate-50 to-blue-50 min-h-screen font-sans">
      <div className="container mx-auto px-4 py-16 max-w-5xl">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
            Currently in Beta: Laptops
          </div>
          <h1 className="text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
            About <span className="text-blue-600">GnosisFT</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            The first step toward a universal, constraint-aware AI shopping assistant.
          </p>
        </div>

        {/* The Vision / Scalability */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">The Vision: Beyond Laptops</h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            We are starting with <strong>Laptops</strong> because they are complex products with rigid hardware constraints—the perfect stress test for our architecture.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            However, the underlying engine is <strong>category-agnostic</strong>. Our "Intent Classification" and "Constraint Extraction" pipelines are designed to scale. The goal is to apply this same rigorous logic to every department:
          </p>
          
          {/* Scalability Icons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="p-4 bg-blue-50 rounded-xl text-center border border-blue-100">
              <span className="text-2xl block mb-2">💻</span>
              <span className="font-bold text-slate-700">Laptops</span>
              <span className="block text-xs text-blue-600 font-semibold mt-1">Live Now</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100 opacity-70">
              <span className="text-2xl block mb-2">📚</span>
              <span className="font-bold text-slate-600">Books</span>
              <span className="block text-xs text-slate-400 mt-1">Coming Soon</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100 opacity-70">
              <span className="text-2xl block mb-2">📺</span>
              <span className="font-bold text-slate-600">TVs</span>
              <span className="block text-xs text-slate-400 mt-1">Roadmap</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100 opacity-70">
              <span className="text-2xl block mb-2">🎧</span>
              <span className="font-bold text-slate-600">Audio</span>
              <span className="block text-xs text-slate-400 mt-1">Roadmap</span>
            </div>
          </div>
        </div>

        {/* Transparency Section: Development Stats */}
        <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Built With Transparency</h2>
            <p className="text-center text-slate-600 mb-8 max-w-2xl mx-auto">
                We believe in honest engineering. This platform was built using a hybrid workflow, leveraging AI for speed and Human engineering for precision.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
            
            {/* CARD 1: Frontend (High AI) */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-purple-500 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-slate-800">Frontend UI</h3>
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">React + Tailwind</span>
                </div>
                
                <div className="space-y-6">
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-slate-600 flex items-center">🤖 AI Generated</span>
                            <span className="text-xl font-bold text-slate-800">80%</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className="bg-purple-500 h-3 rounded-full" style={{ width: '80%' }}></div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Rapid component scaffolding, CSS styling, and boilerplate.</p>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-slate-600 flex items-center">👨‍💻 Human Refined</span>
                            <span className="text-xl font-bold text-slate-800">20%</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className="bg-blue-500 h-3 rounded-full" style={{ width: '20%' }}></div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">UX logic, state management, and final polish.</p>
                    </div>
                </div>
            </div>

            {/* CARD 2: Backend (High Human) */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-blue-600 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-slate-800">Backend Logic</h3>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">FastAPI + LangChain</span>
                </div>
                
                <div className="space-y-6">
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-slate-600 flex items-center">🤖 AI Assisted</span>
                            <span className="text-xl font-bold text-slate-800">10%</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className="bg-purple-500 h-3 rounded-full" style={{ width: '10%' }}></div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Docstring generation and syntax autocomplete.</p>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-slate-600 flex items-center">👨‍💻 Human Engineered</span>
                            <span className="text-xl font-bold text-slate-800">90%</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className="bg-blue-600 h-3 rounded-full" style={{ width: '90%' }}></div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">System architecture, constraint extraction algorithms, and database design.</p>
                    </div>
                </div>
            </div>
            </div>
        </div>

        {/* The Author Section */}
        <div className="bg-slate-900 rounded-3xl shadow-2xl p-8 md:p-12 text-white relative overflow-hidden flex flex-col md:flex-row items-center gap-10">
            
            {/* Left Column: Text */}
            <div className="flex-1 relative z-10 text-center md:text-left order-2 md:order-1">
                <h2 className="text-sm font-bold tracking-widest text-blue-400 uppercase mb-2">Meet the Engineer</h2>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">Faruk Tamyurek</h3>
                <div className="h-1 w-20 bg-blue-500 rounded-full mb-6 mx-auto md:mx-0"></div>
                <p className="text-slate-300 mb-8 leading-relaxed text-lg">
                    M.S. in Electrical Engineering with a focus on AI Systems and Large Language Models. 
                    I built GnosisFT to prove that AI applications can be trustworthy, interpretable, and precise.
                </p>
                
                <div className="flex gap-4 justify-center md:justify-start">
                    <a 
                      href="https://www.linkedin.com/in/faruk-tamy%C3%BCrek-90a53819b/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-[#0077b5] hover:bg-[#006396] text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:-translate-y-1 flex items-center gap-2 shadow-lg"
                    >
                       <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                       LinkedIn
                    </a>
                    
                    <a 
                      href="https://github.com/ftmyrk" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-slate-700 border border-slate-600 hover:bg-slate-600 text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:-translate-y-1 flex items-center gap-2 shadow-lg"
                    >
                       <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                       GitHub
                    </a>
                </div>
            </div>

            {/* Right Column: Photo */}
            <div className="relative z-10 order-1 md:order-2">
                <div className="relative w-56 h-56 mx-auto">
                    {/* Glowing effect behind the image */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-500 to-purple-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                    
                    {/* The Image Itself */}
                    <img 
                        src={farukProfilePic}
                        alt="Faruk Tamyurek" 
                        className="w-half h-half object-cover rounded-full border-4 border-slate-800 shadow-2xl relative z-10"
                    />
                </div>
            </div>
            
            {/* Background pattern */}
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-80 h-80 bg-blue-600 opacity-10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-80 h-80 bg-purple-600 opacity-10 rounded-full blur-3xl"></div>
        </div>

      </div>
    </div>
  );
}